<template>
  <div class="container px-4 py-8 mx-auto">
    <!-- Loading state -->
    <div v-if="loading" class="flex justify-center items-center min-h-[50vh]">
      <ProgressSpinner class="w-12 h-12" strokeWidth="4" />
    </div>

    <div v-else-if="error" class="relative px-4 py-3 mb-6 text-red-700 bg-red-50 rounded border border-red-200">
      {{ error }}
    </div>

    <div v-else>
      <!-- Back button and header -->
      <div class="flex flex-col justify-between mb-6 sm:flex-row sm:items-center">
        <div class="flex justify-end items-center space-x-2">
          <span
            :class="getPaymentStatusClass(payment.status)"
            class="px-3 py-1 text-sm font-medium rounded-full">
            {{ getPaymentStatus(payment.status) }}
          </span>
        </div>
      </div>

      <!-- Payment Information Card -->
      <div class="p-6 mb-6 bg-white rounded-lg shadow-md dark:bg-gray-800">
        <h2 class="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
          <i class="mr-2 pi pi-wallet"></i> Payment Information
        </h2>
        <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <dl class="space-y-3">
              <div>
                <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Transaction ID</dt>
                <dd class="mt-1 text-sm font-medium text-gray-900 dark:text-white">{{ payment.transaction_id }}</dd>
              </div>
              <div>
                <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Payment Method</dt>
                <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ payment.payment_method || 'Card Payment' }}</dd>
              </div>
              <div>
                <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Payment Date</dt>
                <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ formatDate(payment.created_at) }}</dd>
              </div>
            </dl>
          </div>
          <div>
            <dl class="space-y-3">
              <div>
                <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Total Amount</dt>
                <dd class="mt-1 text-lg font-bold text-gray-900 dark:text-white">{{ formatCurrency(payment.amount) }}</dd>
              </div>
              <div>
                <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Service Fee</dt>
                <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ payment.order?.service_fee || 0 }}</dd>
              </div>
              <div>
                <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Delivery Fee</dt>
                <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ formatCurrency(payment.order?.delivery_fee || 0) }}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      <!-- Order Information Card -->
      <div class="p-6 mb-6 bg-white rounded-lg shadow-md dark:bg-gray-800">
        <h2 class="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
          <i class="mr-2 pi pi-shopping-cart"></i> Order Information
        </h2>
        <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <dl class="space-y-3">
              <div>
                <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Order ID</dt>
                <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                  <router-link
                    :to="{ name: ROUTES.DASHBOARD_ORDER, params: { id: payment.order?.id } }"
                    class="font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    #{{ payment.order?.id }}
                  </router-link>
                </dd>
              </div>
              <div>
                <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Order Date</dt>
                <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ formatDate(payment.order?.created_at) }}</dd>
              </div>
              <div>
                <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Order Status</dt>
                <dd class="mt-1">
                  <span
                    :class="getOrderStatusClass(payment.order?.status)"
                    class="inline-flex px-2 text-xs font-semibold leading-5 rounded-full">
                    {{ getOrderStatus(payment.order?.status) }}
                  </span>
                </dd>
              </div>
            </dl>
          </div>
          <div>
            <dl class="space-y-3">
              <div>
                <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Items Count</dt>
                <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ payment.order?.items?.length || 0 }} items</dd>
              </div>
              <div>
                <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Subtotal</dt>
                <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ formatCurrency(payment.order?.subtotal || 0) }}</dd>
              </div>
              <div>
                <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Delivery Address</dt>
                <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ payment.order?.delivery_address || 'N/A' }}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      <!-- Customer and Business Information Cards -->
      <div class="grid grid-cols-1 gap-6 mb-6 md:grid-cols-2">
        <!-- Customer Information -->
        <div class="p-6 bg-white rounded-lg shadow-md dark:bg-gray-800">
          <h2 class="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
            <i class="mr-2 pi pi-user"></i> Customer Information
          </h2>
          <dl class="space-y-3">
            <div>
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Name</dt>
              <dd class="mt-1 text-sm font-medium text-gray-900 dark:text-white">{{ payment.user?.name || 'N/A' }}</dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Email</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ payment.user?.email || 'N/A' }}</dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Phone</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ payment.user?.phone || 'N/A' }}</dd>
            </div>
          </dl>
        </div>

        <!-- Business Information -->
        <div class="p-6 bg-white rounded-lg shadow-md dark:bg-gray-800">
          <h2 class="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
            <i class="mr-2 pi pi-building"></i> Business Information
          </h2>
          <dl class="space-y-3">
            <div>
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Business Name</dt>
              <dd class="mt-1 text-sm font-medium text-gray-900 dark:text-white">{{ payment.order?.business?.name || 'N/A' }}</dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Business Email</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ payment.order?.business?.email || 'N/A' }}</dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Business Phone</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ payment.order?.business?.phone || 'N/A' }}</dd>
            </div>
          </dl>
        </div>
      </div>

      <!-- Order Items -->
      <div class="p-6 mb-6 bg-white rounded-lg shadow-md dark:bg-gray-800" v-if="payment.order?.items?.length">
        <h2 class="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
          <i class="mr-2 pi pi-list"></i> Order Items
        </h2>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead class="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase dark:text-gray-300">Item</th>
                <th class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase dark:text-gray-300">Quantity</th>
                <th class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase dark:text-gray-300">Price</th>
                <th class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase dark:text-gray-300">Total</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
              <tr v-for="item in payment.order?.items" :key="item.id">
                <td class="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap dark:text-white">{{ item.product?.name || item.name }}</td>
                <td class="px-6 py-4 text-sm text-gray-500 whitespace-nowrap dark:text-gray-300">{{ item.quantity }}</td>
                <td class="px-6 py-4 text-sm text-gray-500 whitespace-nowrap dark:text-gray-300">{{ formatCurrency(item.price) }}</td>
                <td class="px-6 py-4 text-sm text-gray-500 whitespace-nowrap dark:text-gray-300">{{ formatCurrency(item.price * item.quantity) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Payment Actions -->
      <div class="flex justify-end mt-8 space-x-4">
        <Button
          v-if="payment.status === 'pending'"
          severity="success"
          label="Mark as Paid"
          icon="pi pi-check"
          :loading="updating"
          @click="updatePaymentStatus('paid')"
        />
        <Button
          v-if="payment.status === 'pending'"
          severity="danger"
          label="Mark as Failed"
          icon="pi pi-times"
          :loading="updating"
          @click="updatePaymentStatus('failed')"
        />
        <!-- <Button
          severity="secondary"
          label="Print Receipt"
          icon="pi pi-print"
          @click="printReceipt" -->
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { ROUTES } from '@/router/routeNames';
import { formatCurrency, formatDate } from '@/utils';
import { getPaymentStatus, getPaymentStatusClass } from '@/utils/payments';
import { getOrderStatus, getStatusClass as getOrderStatusClass } from '@/utils/orders';
import ProgressSpinner from 'primevue/progressspinner';
import Button from 'primevue/button';
import api from '@/services/axios';

const route = useRoute();
const router = useRouter();
const toast = useToast();

const payment = ref({});
const loading = ref(true);
const updating = ref(false);
const error = ref(null);

onMounted(async () => {
  await fetchPayment();
});

async function fetchPayment() {
  loading.value = true;
  error.value = null;

  try {
    const response = await axios.get(`/api/admin/payments/${route.params.id}`);
    payment.value = response.data;
  } catch (err) {
    error.value = 'Failed to load payment details. Please try again.';
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load payment details',
      life: 3000
    });
    console.error(err);
  } finally {
    loading.value = false;
  }
}

async function updatePaymentStatus(status) {
  updating.value = true;

  try {
    const response = await api.patch(`/api/admin/payments/${route.params.id}`, {
      status
    });

    payment.value = response.data;

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: `Payment marked as ${status}`,
      life: 3000
    });
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update payment status',
      life: 3000
    });
    console.error(err);
  } finally {
    updating.value = false;
  }
}

function printReceipt() {
  window.print();
}
</script>

<!-- <style lang="scss" scoped>
@media print {
  button, .pi-arrow-left {
    display: none !important;
  }

  .container {
    width: 100%;
    max-width: 100%;
    padding: 0;
    margin: 0;
  }
}
</style> -->
